/**
 * ALERON cart drawer.
 *
 * Quantity changes and removals go through the Ajax Cart API. Each request
 * asks Shopify to re-render the drawer section alongside the mutation, so the
 * markup comes back from Liquid rather than being rebuilt in JS — one source
 * of truth for the line items and the totals.
 *
 * The drawer is a <dialog>, so focus trapping, Escape and background inerting
 * come from the platform (see assets/aleron-overlay.js).
 */

import { AleronOverlay, define } from '@theme/aleron-overlay';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/** The section whose HTML is re-rendered after every mutation. */
const SECTION_ID = 'cart-drawer-section';

class AleronCart extends AleronOverlay {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);

    // Another component (quick add, the product page) may have changed the
    // cart; re-render so the drawer is never stale when it opens.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleExternalUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleExternalUpdate);
  }

  #handleExternalUpdate = (event) => {
    event.promise
      ?.then((result) => {
        // Ignore the echo of a change this drawer made itself — it has already
        // swapped in fresh markup from the same response. The source travels
        // in the promise's resolved detail, not on the event: checking
        // event.detail.source never matched, so every +/- tap fetched and
        // re-rendered the whole drawer a second time.
        if (result?.detail?.source === 'aleron-cart') return;
        return this.#render();
      })
      .catch(() => {});
  };

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const remove = target.closest('[data-aleron-cart-remove]');
    if (remove) {
      event.preventDefault();
      this.#change(remove.dataset.lineKey, 0);
      return;
    }

    const step = target.closest('[data-aleron-cart-step]');
    if (step) {
      event.preventDefault();
      const next = Number(step.dataset.quantity) + Number(step.dataset.aleronCartStep);
      // Stepping below one is a removal; the minus button is disabled at one,
      // so this only guards a stale DOM.
      this.#change(step.dataset.lineKey, Math.max(0, next));
    }
  };

  #handleChange = (event) => {
    const input = event.target instanceof Element ? event.target.closest('[data-aleron-cart-quantity]') : null;
    if (!input) return;

    const quantity = Number.parseInt(input.value, 10);
    if (!Number.isFinite(quantity) || quantity < 0) {
      // Restore what the server last told us rather than sending nonsense.
      input.value = input.defaultValue;
      return;
    }

    this.#change(input.dataset.lineKey, quantity);
  };

  /**
   * @param {string} key  Line item key. Used rather than the line index,
   *                      which shifts as soon as another line is removed.
   * @param {number} quantity
   */
  async #change(key, quantity) {
    if (!key || this.busy) return;

    this.busy = true;
    this.#setBusy(true);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: quantity === 0 ? 'remove' : 'update',
        context: 'cart',
        lines: [{ merchandiseId: key, quantity }],
        promise: deferred.promise
      })
    );

    try {
      const response = await fetch(`${window.Theme?.routes?.cart_change_url || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: SECTION_ID })
      });

      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
      const cart = await response.json();

      this.#replaceContent(cart.sections?.[SECTION_ID]);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'aleron-cart',
          sourceId: this.id || 'cart-drawer',
          itemCount: cart.item_count,
          didError: false
        }
      });
    } catch (error) {
      deferred.reject(error);
      this.#showError(error);
    } finally {
      this.busy = false;
      this.#setBusy(false);
    }
  }

  /**
   * The busy styles target the <dialog> (.aleron-cart[data-busy]), which is
   * what carries the .aleron-cart class. The attribute used to be set on this
   * host element instead, so the fade and the progress line never appeared.
   */
  #setBusy(busy) {
    for (const el of [this, this.dialog]) el?.toggleAttribute('data-busy', busy);
  }

  /** Re-renders the drawer from the section HTML, without closing it. */
  async #render() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=${SECTION_ID}`);
      if (!response.ok) return;
      this.#replaceContent(await response.text());
    } catch {
      // A failed refresh leaves the previous markup, which is still valid.
    }
  }

  #replaceContent(html) {
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('[data-aleron-cart-content]');
    const current = this.querySelector('[data-aleron-cart-content]');
    if (!next || !current) return;

    // Replacing only the contents keeps the <dialog> element itself — and so
    // its open state and the focus inside it — untouched.
    current.replaceWith(next);
  }

  #showError(error) {
    const region = this.querySelector('[data-aleron-cart-error]');
    if (!region) return;
    region.textContent = error?.message || '';
    region.hidden = false;
  }
}

define('aleron-cart', AleronCart);
